import { T } from 'gt-react';
import { SpinnerIcon } from '@phosphor-icons/react';
import { useOutboundEmail } from '@/api/rpc/hooks/communications/useOutboundEmail';
import { EmailViewerV2 } from '@/components/email-viewer/email-viewer-v2';
import type { EmailData } from '@/components/email-viewer/email-viewer-types';

/**
 * Shared plumbing for rendering a sent (outbound) email. Loads it by id and
 * maps it into the EmailViewerV2 shape. Extracted so multiple surfaces can show
 * the same content without duplicating the data/mapping/render logic — currently
 * OutboundEmailDetailSheet (side sheet) and the bulk statement send dialog's
 * read-only "sent" review.
 */
export function useOutboundEmailViewData(
  outboundEmailId: string | undefined,
  enabled: boolean,
): { emailData: EmailData | undefined; isLoading: boolean; subject?: string } {
  const { outboundEmail, isLoading } = useOutboundEmail(
    outboundEmailId,
    enabled,
  );

  const emailData: EmailData | undefined = outboundEmail
    ? {
        subject: outboundEmail.subject,
        from: outboundEmail.from,
        to: outboundEmail.to,
        cc: outboundEmail.cc ?? undefined,
        htmlContent: outboundEmail.htmlContent ?? undefined,
        textContent: outboundEmail.content,
        date: outboundEmail.sentAt?.toString(),
        attachments: outboundEmail.attachments?.map((a) => ({
          id: a.id,
          fileName: a.fileName,
          sizeBytes: a.sizeBytes,
          mimeType: a.mimeType,
          s3Url: a.s3Url,
        })),
      }
    : undefined;

  return { emailData, isLoading, subject: outboundEmail?.subject };
}

/**
 * Loading / empty / EmailViewerV2 body for a sent email. Shell-agnostic: the
 * caller supplies a bounded-height container (a Sheet or Dialog content region).
 */
export function OutboundEmailViewerBody({
  emailData,
  isLoading,
}: {
  emailData: EmailData | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!emailData) {
    return (
      <div className="px-4 py-12 text-sm text-muted-foreground text-center">
        <T context="sent email preview unavailable">No preview available</T>
      </div>
    );
  }
  return <EmailViewerV2 emailData={emailData} />;
}
